import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { getRequestHeader, getRequestIP } from "@tanstack/react-start/server";

const schema = z.object({
  email: z.string().max(320).nullable().optional(),
  name: z.string().max(200).nullable().optional(),
  phone: z.string().max(40).nullable().optional(),
  stage: z.string().min(1).max(60),
  status: z.enum(["attempt", "success", "failure"]),
  error_code: z.string().max(100).nullable().optional(),
  error_message: z.string().max(2000).nullable().optional(),
  inquiry_id: z.string().uuid().nullable().optional(),
  session_id: z.string().max(100).nullable().optional(),
  path: z.string().max(1000).nullable().optional(),
  payload: z.unknown().optional(),
  metadata: z.unknown().optional(),
});

/**
 * Server-side audit log for opt-in submissions. Writes via the admin client
 * so client-side failures (RLS, validation, network) are still captured.
 * Never throws — logging must not break the user flow.
 */
export const logSubmissionAttempt = createServerFn({ method: "POST" })
  .inputValidator((input) => schema.parse(input))
  .handler(async ({ data }) => {
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const ua = getRequestHeader("user-agent") ?? null;
      let ip: string | null = null;
      try {
        ip = getRequestIP({ xForwardedFor: true }) ?? null;
      } catch {
        ip = null;
      }
      const { error } = await supabaseAdmin.from("submission_attempts").insert({
        email: data.email ?? null,
        name: data.name ?? null,
        phone: data.phone ?? null,
        stage: data.stage,
        status: data.status,
        error_code: data.error_code ?? null,
        error_message: data.error_message ?? null,
        inquiry_id: data.inquiry_id ?? null,
        session_id: data.session_id ?? null,
        path: data.path ?? null,
        user_agent: ua,
        ip,
        payload: (data.payload ?? null) as never,
        metadata: (data.metadata ?? null) as never,
      });
      if (error) {
        console.error("[submission-log] insert failed:", error);
        return { ok: false as const, error: error.message };
      }
      // Structured server log line for quick grep by email/timestamp.
      console.log(
        `[submission] ${data.status} stage=${data.stage} email=${data.email ?? "-"} ` +
          `inquiry_id=${data.inquiry_id ?? "-"} err=${data.error_message ?? "-"}`,
      );
      return { ok: true as const };
    } catch (e) {
      console.error("[submission-log] exception:", e);
      return { ok: false as const, error: (e as Error).message };
    }
  });
